import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import SignupTimelineSection from 'src/common/components/signup-timeline/SignupTimelineSection';
import {
  DashboardPageToolbar,
  DashboardToolbarAside,
} from 'src/common/components/DashboardPageToolbar';
import { frostedPanelSx } from 'src/common/styles/frostedPanelStyles';
import { IEdition } from 'src/interfaces/Edition';
import {
  getEditionsWithAccountingData,
  hasEditionAccountingData,
} from 'src/pages/Dashboard/Editions/utils/editionAccounting';

import EditionRevenueChart from './EditionRevenueChart';
import EditionTotalsSummary from './EditionTotalsSummary';

interface Props {
  editions: IEdition[];
  selectedEdition?: IEdition;
  toolbar?: ReactNode;
}

const accountingPaperSx = {
  ...frostedPanelSx(0.5),
  width: '100%',
  p: 2,
  mt: 1,
} as const;

const EditionAccountingSection = ({ editions, selectedEdition, toolbar }: Props) => {
  const { t } = useTranslation();

  const editionsWithData = getEditionsWithAccountingData(editions);
  const selectedHasData =
    selectedEdition != null && hasEditionAccountingData(selectedEdition);

  return (
    <Box component="section" sx={{ mt: 3, width: '100%' }}>
      <Typography variant="h2" component="h2" sx={{ mb: 1 }}>
        {t('editions.accounting.title')}
      </Typography>

      {toolbar ? (
        <DashboardPageToolbar>
          <DashboardToolbarAside variant="grow">{toolbar}</DashboardToolbarAside>
        </DashboardPageToolbar>
      ) : null}

      <Paper sx={accountingPaperSx}>
        {!selectedEdition ? (
          <Typography variant="body2" color="text.secondary">
            {t('editions.accounting.selectEditionInToolbar')}
          </Typography>
        ) : (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="subtitle1" component="h3" sx={{ mb: 1 }}>
                {t('editions.accounting.summaryTitle')}
                {` (${selectedEdition.year})`}
              </Typography>
              {selectedHasData && selectedEdition.checkinsByPrice != null ? (
                <EditionTotalsSummary
                  checkinsByPrice={selectedEdition.checkinsByPrice}
                  totalPrice={selectedEdition.totalPrice as number}
                  editionPrices={selectedEdition}
                  disableGutters
                  emphasizeTotal
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('editions.accounting.editionHasNoData')}
                </Typography>
              )}
              {!selectedHasData && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('editions.accounting.saveStatisticsHint')}
                </Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 9 }}>
              <Typography variant="subtitle1" component="h3" sx={{ mb: 1 }}>
                {t('editions.accounting.chartTitle')}
              </Typography>
              {editionsWithData.length > 0 ? (
                <EditionRevenueChart
                  editions={editions}
                  selectedEditionId={selectedEdition.id}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('editions.accounting.noSavedData')}
                </Typography>
              )}
            </Grid>
          </Grid>
        )}
      </Paper>

      {selectedEdition ? (
        <Box sx={{ mt: 2 }}>
          <SignupTimelineSection edition={selectedEdition} includeUnpublished />
        </Box>
      ) : null}
    </Box>
  );
};

export default EditionAccountingSection;
